import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Calculator,
  Landmark,
  Scale,
  Calendar,
  DollarSign,
  Download,
  Copy,
  Plus,
  Trash2,
  Sparkles,
  FileSpreadsheet,
  CheckCircle2,
  TrendingUp,
  FileText,
  Percent,
  RefreshCw,
  Building2,
  UserCheck
} from 'lucide-react';
import {
  HISTORICO_TASAS_SFC,
  getOfficialRateForDate,
  convertEAToEM,
  OfficialRateRecord
} from './SuperfinancieraRatesData';
import {
  RatePolicy,
  DebtItem,
  JudicialSettlementRow,
  JudicialSettlement
} from './LiquidadorTypes';
import { toast } from 'sonner';

interface DebtorOption {
  id?: string;
  unidad_privada: string;
  propietario_nombre: string;
  propietario_id?: string;
  telefono?: string;
  email?: string;
  saldo_capital: number;
  saldo_intereses: number;
  dias_mora?: number;
}

interface FinancialLiquidadorJudicialProps {
  debtors?: DebtorOption[];
  copropiedadNombre?: string;
  copropiedadNit?: string;
  onApplyCalculatedSaldos?: (capital: number, intereses: number, unidad: string) => void;
}

export const FinancialLiquidadorJudicial: React.FC<FinancialLiquidadorJudicialProps> = ({
  debtors = [],
  copropiedadNombre = 'Conjunto Residencial Torres del Parque',
  copropiedadNit = '900.852.147-3',
  onApplyCalculatedSaldos
}) => {
  // Datos del Deudor
  const [selectedDebtorId, setSelectedDebtorId] = useState<string>('');
  const [unidadPrivada, setUnidadPrivada] = useState('Torre 2 Apto 504');
  const [propietarioNombre, setPropietarioNombre] = useState('Carlos Eduardo Restrepo');
  const [propietarioId, setPropietarioId] = useState('79.845.123');

  // Política de Tasa
  const [politicaTasa, setPoliticaTasa] = useState<RatePolicy>('maxima_usura_sfc');
  const [tasaFijaAsamblea, setTasaFijaAsamblea] = useState<number>(1.8); // 1.8% mensual

  // Fecha de Corte para la Liquidación Judicial
  const [fechaCorte, setFechaCorte] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });

  // Lista de cuotas adeudadas
  const [debtItems, setDebtItems] = useState<DebtItem[]>([
    { id: '1', concepto: 'Administración Mayo 2026', fechaExigibilidad: '2026-05-11', capital: 380000, abono: 0 },
    { id: '2', concepto: 'Administración Junio 2026', fechaExigibilidad: '2026-06-11', capital: 380000, abono: 0 },
    { id: '3', concepto: 'Administración Julio 2026', fechaExigibilidad: '2026-07-11', capital: 380000, abono: 0 },
    { id: '4', concepto: 'Administración Agosto 2026', fechaExigibilidad: '2026-08-11', capital: 380000, abono: 0 },
    { id: '5', concepto: 'Administración Septiembre 2026', fechaExigibilidad: '2026-09-11', capital: 380000, abono: 0 },
    { id: '6', concepto: 'Administración Octubre 2026', fechaExigibilidad: '2026-10-11', capital: 380000, abono: 0 }
  ]);

  // Generador Rápido de Cuotas
  const [cuotaGeneradorMonto, setCuotaGeneradorMonto] = useState<number>(380000);
  const [cuotasGeneradorCantidad, setCuotasGeneradorCantidad] = useState<number>(6);

  // Modo de Vista: Tabla Interactiva vs. Memorial para Juzgado
  const [viewMode, setViewMode] = useState<'tabla' | 'memorial' | 'tasas_sfc'>('tabla');

  // Selección de deudor del directorio
  const handleSelectDebtor = (debtorKey: string) => {
    setSelectedDebtorId(debtorKey);
    const debtor = debtors.find(d => (d.id === debtorKey || d.unidad_privada === debtorKey));
    if (debtor) {
      setUnidadPrivada(debtor.unidad_privada);
      setPropietarioNombre(debtor.propietario_nombre);
      setPropietarioId(debtor.propietario_id || '');

      // Distribuir el capital adeudado en cuotas mensuales estimadas
      const cap = debtor.saldo_capital || 1200000;
      const mesesEst = Math.max(1, Math.min(12, Math.round(cap / 380000)));
      generateBatchCuotas(mesesEst, Math.round(cap / mesesEst));
      toast.success(`Datos de ${debtor.unidad_privada} cargados al liquidador.`);
    }
  };

  // Función para generar lote de cuotas en orden retrospectivo
  const generateBatchCuotas = (cantidad: number, monto: number) => {
    const today = new Date();
    const newItems: DebtItem[] = [];
    const mesesNombres = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];

    for (let i = cantidad - 1; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 11);
      const mName = mesesNombres[d.getMonth()];
      const y = d.getFullYear();
      newItems.push({
        id: `cuota_${i}_${Date.now()}`,
        concepto: `Administración ${mName} ${y}`,
        fechaExigibilidad: d.toISOString().split('T')[0],
        capital: monto,
        abono: 0
      });
    }

    setDebtItems(newItems);
  };

  // Agregar cuota individual
  const handleAddSingleCuota = () => {
    const nextIdx = debtItems.length + 1;
    const newItem: DebtItem = {
      id: `manual_${Date.now()}`,
      concepto: `Expensa Cuota #${nextIdx}`,
      fechaExigibilidad: new Date().toISOString().split('T')[0],
      capital: cuotaGeneradorMonto,
      abono: 0
    };
    setDebtItems([...debtItems, newItem]);
  };

  // Eliminar cuota
  const handleRemoveCuota = (id: string) => {
    setDebtItems(debtItems.filter(item => item.id !== id));
  };

  // Actualizar valores de cuota
  const handleUpdateCuota = (id: string, field: 'concepto' | 'fechaExigibilidad' | 'capital' | 'abono', value: any) => {
    setDebtItems(debtItems.map(item => {
      if (item.id === id) {
        return { ...item, [field]: value };
      }
      return item;
    }));
  };

  // ── MOTOR FINANCIERO JUDICIAL DE LIQUIDACIÓN MES A MES (ART. 446 CGP) ──
  const settlementResult = useMemo(() => {
    const corteDate = new Date(fechaCorte);
    let totalCap = 0;
    let totalIntereses = 0;
    let totalAbonos = 0;
    const rows: JudicialSettlementRow[] = [];

    debtItems.forEach((item, index) => {
      const exigibleDate = new Date(item.fechaExigibilidad);
      const capitalNeto = Math.max(0, item.capital - (item.abono || 0));
      totalCap += item.capital;
      totalAbonos += (item.abono || 0);

      // Si la fecha de corte es anterior o igual a la exigibilidad, no hay mora
      const diffTime = corteDate.getTime() - exigibleDate.getTime();
      const diffDays = Math.max(0, Math.floor(diffTime / (1000 * 60 * 60 * 24)));

      if (diffDays <= 0 || capitalNeto <= 0) {
        rows.push({
          numero: index + 1,
          concepto: item.concepto,
          fechaExigibilidad: item.fechaExigibilidad,
          fechaCorte,
          diasMora: 0,
          capitalBase: capitalNeto,
          tasaEA: 0,
          tasaEM: 0,
          interesMora: 0,
          abonosImputados: item.abono || 0,
          saldoFinalFila: capitalNeto,
          resolucionSFC: 'Al Día'
        });
        return;
      }

      // Obtener la tasa oficial del mes de exigibilidad
      const rateInfo = getOfficialRateForDate(exigibleDate.getFullYear(), exigibleDate.getMonth() + 1);

      let tasaEA = rateInfo.usura_ea;
      let tasaEM = rateInfo.tasa_efectiva_mensual;
      let resolucion = rateInfo.resolucion_sfc || 'SFC';

      if (politicaTasa === 'ibc_corriente_sfc') {
        tasaEA = rateInfo.ibc_ea;
        tasaEM = convertEAToEM(rateInfo.ibc_ea);
        resolucion = `1.0x IBC (${rateInfo.resolucion_sfc || 'SFC'})`;
      } else if (politicaTasa === 'tasa_fija_asamblea') {
        tasaEM = tasaFijaAsamblea;
        tasaEA = +((Math.pow(1 + tasaFijaAsamblea / 100, 12) - 1) * 100).toFixed(2);
        resolucion = 'Tasa Fija Asamblea PH';
      }

      // Cálculo de interés compuesto o proporcional a días
      // Fórmula judicial colombiana: I = Capital * [(1 + i_em)^(dias/30) - 1] o Tasa Diaria
      const tasaDiaria = Math.pow(1 + (tasaEM / 100), 1 / 30) - 1;
      const interesCalculado = Math.round(capitalNeto * (Math.pow(1 + tasaDiaria, diffDays) - 1));

      totalIntereses += interesCalculado;

      rows.push({
        numero: index + 1,
        concepto: item.concepto,
        fechaExigibilidad: item.fechaExigibilidad,
        fechaCorte,
        diasMora: diffDays,
        capitalBase: capitalNeto,
        tasaEA,
        tasaEM,
        interesMora: interesCalculado,
        abonosImputados: item.abono || 0,
        saldoFinalFila: capitalNeto + interesCalculado,
        resolucionSFC: resolucion
      });
    });

    const totalCredito = totalCap - totalAbonos + totalIntereses;
    const tasaPromedioPonderada = rows.length > 0
      ? +(rows.reduce((acc, r) => acc + r.tasaEM, 0) / rows.length).toFixed(2)
      : 2.05;

    return {
      totalCapital: totalCap,
      totalAbonos,
      totalInteresesMora: totalIntereses,
      totalLiquidacionCredito: totalCredito,
      tasaPromedioPonderada,
      filas: rows
    };
  }, [debtItems, fechaCorte, politicaTasa, tasaFijaAsamblea]);

  // Formato Memorial Judicial para los Jueces Civiles (Art. 446 CGP)
  const memorialJudicialTexto = useMemo(() => {
    return `LIQUIDACIÓN JUDICIAL DE CRÉDITO Y COSTAS (ARTÍCULO 446 DEL CÓDIGO GENERAL DEL PROCESO)
PROCESO: EJECUTIVO SINGULAR DE MÍNIMA CUANTÍA
DEMANDANTE: ${copropiedadNombre.toUpperCase()} (NIT: ${copropiedadNit})
DEMANDADO(A): ${propietarioNombre.toUpperCase()} (C.C./NIT: ${propietarioId})
INMUEBLE / UNIDAD PRIVADA: ${unidadPrivada.toUpperCase()}
FECHA DE CORTE DE LIQUIDACIÓN: ${fechaCorte}

Señor(a) Juez:
En cumplimiento de lo ordenado en el mandamiento de pago y de conformidad con el artículo 446 del Código General del Proceso y el artículo 30 de la Ley 675 de 2001, presento a su Despacho la liquidación detallada del crédito a la fecha de corte mencionada:

I. RESUMEN CONSOLIDADO DEL CRÉDITO:
1. Total Saldo de Capital Insoluto: $${settlementResult.totalCapital.toLocaleString('es-CO')} COP
2. Menos Abonos Imputados a Capital: $${settlementResult.totalAbonos.toLocaleString('es-CO')} COP
3. Total Intereses Moratorios Liquidados: $${settlementResult.totalInteresesMora.toLocaleString('es-CO')} COP
   (Liquidados a la tasa máxima legal certificada mensualmente por la Superintendencia Financiera de Colombia)
TOTAL CRÉDITO LIQUIDADO APROBADO: $${settlementResult.totalLiquidacionCredito.toLocaleString('es-CO')} COP

II. DISCRIMINACIÓN CUOTA POR CUOTA Y TASAS OFICIALES SUPERFINANCIERA (SFC):
${settlementResult.filas.map(f => {
  return `• Cuota #${f.numero} - ${f.concepto}:
  - Exigible desde: ${f.fechaExigibilidad} | Días de mora a corte: ${f.diasMora} días
  - Capital base: $${f.capitalBase.toLocaleString('es-CO')} COP
  - Tasa aplicada: ${f.tasaEA}% E.A. (${f.tasaEM}% E.M.) [${f.resolucionSFC}]
  - Interés moratorio causado: $${f.interesMora.toLocaleString('es-CO')} COP
  - Saldo acumulado cuota: $${f.saldoFinalFila.toLocaleString('es-CO')} COP`;
}).join('\n\n')}

III. IMPUTACIÓN DEL PAGO:
Se deja expresa constancia de que los abonos eventuales han sido imputados conforme a la regla del artículo 1653 del Código Civil Colombiano: primero a intereses causados y el excedente a capital.

Solicito al Despacho impartir aprobación a la presente liquidación en los términos de ley.

Atentamente,

___________________________________________________
APODERADO JUDICIAL - DEMANDANTE
${copropiedadNombre}`;
  }, [copropiedadNombre, copropiedadNit, propietarioNombre, propietarioId, unidadPrivada, fechaCorte, settlementResult]);

  const copyMemorial = () => {
    navigator.clipboard.writeText(memorialJudicialTexto);
    toast.success('Liquidación en formato judicial copiada al portapapeles.');
  };

  const downloadMemorialTxt = () => {
    const blob = new Blob([memorialJudicialTexto], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Liquidacion_Judicial_${unidadPrivada.replace(/\s+/g, '_')}_${fechaCorte}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Liquidación descargada en TXT.');
  };

  const exportExcelCSV = () => {
    const headers = ['# Cuota', 'Concepto', 'Fecha Exigibilidad', 'Fecha Corte', 'Dias Mora', 'Capital Base', 'Tasa EA %', 'Tasa EM %', 'Interes Mora COP', 'Saldo Final COP', 'Resolucion SFC'];
    const rowsData = settlementResult.filas.map(f => [
      f.numero,
      `"${f.concepto}"`,
      f.fechaExigibilidad,
      f.fechaCorte,
      f.diasMora,
      f.capitalBase,
      f.tasaEA,
      f.tasaEM,
      f.interesMora,
      f.saldoFinalFila,
      `"${f.resolucionSFC}"`
    ]);

    const csvContent = [headers.join(','), ...rowsData.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Liquidacion_Superfinanciera_${unidadPrivada.replace(/\s+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Planilla CSV descargada con éxito.');
  };

  const handleApplyToForm = () => {
    if (onApplyCalculatedSaldos) {
      onApplyCalculatedSaldos(
        settlementResult.totalCapital,
        settlementResult.totalInteresesMora,
        unidadPrivada
      );
      toast.success(`Saldos liquidados ($${settlementResult.totalCapital.toLocaleString('es-CO')} capital + $${settlementResult.totalInteresesMora.toLocaleString('es-CO')} intereses) aplicados.`);
    } else {
      toast.success('Saldos liquidados listos para exportar.');
    }
  };

  return (
    <div className="space-y-6">
      {/* ── HEADER Y PRESENTACIÓN ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-xl bg-gradient-to-r from-slate-900 via-emerald-950 to-slate-900 text-white shadow-md">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-emerald-600 text-white shadow">
            <Calculator className="h-6 w-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base sm:text-lg font-bold">
                Liquidador Financiero &amp; Tasas Oficiales de Usura (Superfinanciera)
              </h2>
              <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-400/30 text-[10px]">
                Art. 30 Ley 675 / Art. 446 CGP
              </Badge>
            </div>
            <p className="text-xs text-slate-300 mt-0.5">
              Cálculo mes a mes cuota por cuota con certificación mensual oficial de la Superintendencia Financiera de Colombia.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={handleApplyToForm}
            className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs h-8 font-semibold gap-1.5 shadow"
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            Aplicar a Cobranza
          </Button>
        </div>
      </div>

      {/* ── TARJETAS DE IMPACTO FINANCIERO LIQUIDADO ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border shadow-xs">
          <CardContent className="p-3.5 flex items-center justify-between">
            <div>
              <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider block">
                Capital en Mora
              </span>
              <span className="text-xl font-bold text-slate-900 dark:text-white">
                ${settlementResult.totalCapital.toLocaleString('es-CO')}
              </span>
              <span className="text-[10px] text-muted-foreground block mt-0.5">
                {debtItems.length} cuotas causadas
              </span>
            </div>
            <div className="p-2.5 rounded-xl bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
              <Building2 className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border shadow-xs border-rose-200 dark:border-rose-900/40">
          <CardContent className="p-3.5 flex items-center justify-between">
            <div>
              <span className="text-[11px] font-medium text-rose-700 dark:text-rose-400 uppercase tracking-wider block">
                Intereses de Mora (SFC)
              </span>
              <span className="text-xl font-black text-rose-600">
                ${settlementResult.totalInteresesMora.toLocaleString('es-CO')}
              </span>
              <span className="text-[10px] text-rose-600 block mt-0.5 font-medium">
                Tasa ponderada: {settlementResult.tasaPromedioPonderada}% E.M.
              </span>
            </div>
            <div className="p-2.5 rounded-xl bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-400">
              <TrendingUp className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border shadow-xs border-emerald-200 dark:border-emerald-900/40 bg-emerald-50/30 dark:bg-emerald-950/20">
          <CardContent className="p-3.5 flex items-center justify-between">
            <div>
              <span className="text-[11px] font-medium text-emerald-800 dark:text-emerald-300 uppercase tracking-wider block">
                Total Liquidación Judicial
              </span>
              <span className="text-2xl font-black text-emerald-700 dark:text-emerald-300">
                ${settlementResult.totalLiquidacionCredito.toLocaleString('es-CO')}
              </span>
              <span className="text-[10px] text-emerald-600 block mt-0.5">
                Capital + Intereses netos
              </span>
            </div>
            <div className="p-2.5 rounded-xl bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400">
              <Scale className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border shadow-xs">
          <CardContent className="p-3.5 flex items-center justify-between">
            <div>
              <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider block">
                Tasa Usura Vigente
              </span>
              <span className="text-xl font-bold text-slate-900 dark:text-white">
                {HISTORICO_TASAS_SFC[0].usura_ea}% EA
              </span>
              <span className="text-[10px] text-emerald-600 block mt-0.5 font-mono">
                {HISTORICO_TASAS_SFC[0].tasa_efectiva_mensual}% Mes Vencido
              </span>
            </div>
            <div className="p-2.5 rounded-xl bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400">
              <Landmark className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* ── COLUMNA IZQUIERDA: PARÁMETROS, AUTOCARGA Y GENERADOR RÁPIDO ── */}
        <div className="lg:col-span-4 space-y-4 text-xs">
          {/* Autocompletar desde Directorio */}
          <Card className="border shadow-sm">
            <CardHeader className="pb-2.5 bg-muted/20">
              <CardTitle className="text-xs font-bold uppercase tracking-wider flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <UserCheck className="h-3.5 w-3.5 text-emerald-600" />
                  Inmueble &amp; Copropietario
                </span>
                <span className="text-[10px] text-muted-foreground font-mono">{debtors.length} registrados</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-3">
              <Select value={selectedDebtorId} onValueChange={handleSelectDebtor}>
                <SelectTrigger className="h-8 text-xs bg-white dark:bg-slate-900">
                  <SelectValue placeholder="Seleccione unidad (ej. Torre 2 Apto 504)..." />
                </SelectTrigger>
                <SelectContent className="max-h-56">
                  {debtors.map((d) => (
                    <SelectItem key={d.id || d.unidad_privada} value={d.id || d.unidad_privada}>
                      {d.unidad_privada} — {d.propietario_nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-[10px]">Unidad Privada</Label>
                  <Input
                    value={unidadPrivada}
                    onChange={(e) => setUnidadPrivada(e.target.value)}
                    className="text-xs h-8"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px]">C.C. / NIT</Label>
                  <Input
                    value={propietarioId}
                    onChange={(e) => setPropietarioId(e.target.value)}
                    className="text-xs h-8 font-mono"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-[10px]">Nombre del Propietario</Label>
                <Input
                  value={propietarioNombre}
                  onChange={(e) => setPropietarioNombre(e.target.value)}
                  className="text-xs h-8"
                />
              </div>
            </CardContent>
          </Card>

          {/* Configuración de Política de Tasas y Fecha de Corte */}
          <Card className="border shadow-sm">
            <CardHeader className="pb-2.5 bg-muted/20">
              <CardTitle className="text-xs font-bold uppercase tracking-wider flex items-center gap-1.5">
                <Percent className="h-3.5 w-3.5 text-blue-600" />
                Política de Tasas &amp; Fecha de Corte
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-3">
              <div className="space-y-1">
                <Label className="text-[10px]">Régimen de Tasa de Interés</Label>
                <Select value={politicaTasa} onValueChange={(val: any) => setPoliticaTasa(val)}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="maxima_usura_sfc">Tasa Máxima de Usura SFC (1.5x IBC - Ley 675)</SelectItem>
                    <SelectItem value="ibc_corriente_sfc">Interés Bancario Corriente (1.0x IBC)</SelectItem>
                    <SelectItem value="tasa_fija_asamblea">Tasa Fija Asamblea PH (Inferior a Usura)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {politicaTasa === 'tasa_fija_asamblea' && (
                <div className="space-y-1 p-2 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200">
                  <Label className="text-[10px] text-blue-900 dark:text-blue-200 font-semibold">Tasa Fija Mensual (% E.M.)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={tasaFijaAsamblea}
                    onChange={(e) => setTasaFijaAsamblea(parseFloat(e.target.value) || 0)}
                    className="text-xs h-8 font-bold"
                  />
                </div>
              )}

              <div className="space-y-1">
                <Label className="text-[10px]">Fecha de Corte de Liquidación</Label>
                <Input
                  type="date"
                  value={fechaCorte}
                  onChange={(e) => setFechaCorte(e.target.value)}
                  className="text-xs h-8 font-mono"
                />
              </div>
            </CardContent>
          </Card>

          {/* Generador Rápido de Cuotas Vencidas */}
          <Card className="border shadow-sm border-emerald-200 dark:border-emerald-900/40">
            <CardHeader className="pb-2.5 bg-emerald-50/50 dark:bg-emerald-950/20">
              <CardTitle className="text-xs font-bold uppercase tracking-wider text-emerald-900 dark:text-emerald-200 flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5 text-emerald-600" />
                  Generador Rápido de Cuotas
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-3">
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-[10px]">Monto por Cuota ($)</Label>
                  <Input
                    type="number"
                    value={cuotaGeneradorMonto}
                    onChange={(e) => setCuotaGeneradorMonto(parseFloat(e.target.value) || 0)}
                    className="text-xs h-8 font-semibold"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px]">Número de Meses</Label>
                  <Select value={String(cuotasGeneradorCantidad)} onValueChange={(val) => setCuotasGeneradorCantidad(parseInt(val, 10))}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3 meses</SelectItem>
                      <SelectItem value="6">6 meses</SelectItem>
                      <SelectItem value="12">12 meses (1 Año)</SelectItem>
                      <SelectItem value="18">18 meses</SelectItem>
                      <SelectItem value="24">24 meses (2 Años)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex gap-2 pt-1">
                <Button
                  size="sm"
                  onClick={() => generateBatchCuotas(cuotasGeneradorCantidad, cuotaGeneradorMonto)}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8 font-medium gap-1"
                >
                  <RefreshCw className="h-3 w-3" /> Generar {cuotasGeneradorCantidad} Cuotas
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleAddSingleCuota}
                  className="text-xs h-8 gap-1"
                  title="Agregar 1 cuota individual"
                >
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ── COLUMNA DERECHA: TABLA DE LIQUIDACIÓN JUDICIAL & HISTÓRICO SFC ── */}
        <div className="lg:col-span-8 space-y-4">
          <Card className="border shadow-sm">
            <CardHeader className="pb-2 border-b bg-muted/20">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                  <Button
                    variant={viewMode === 'tabla' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('tabla')}
                    className={`text-xs h-7 gap-1 ${viewMode === 'tabla' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                  >
                    <Scale className="h-3 w-3" /> Tabla Judicial ({settlementResult.filas.length} Cuotas)
                  </Button>
                  <Button
                    variant={viewMode === 'memorial' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('memorial')}
                    className={`text-xs h-7 gap-1 ${viewMode === 'memorial' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                  >
                    <FileText className="h-3 w-3" /> Memorial Juzgado (Art. 446 CGP)
                  </Button>
                  <Button
                    variant={viewMode === 'tasas_sfc' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('tasas_sfc')}
                    className={`text-xs h-7 gap-1 ${viewMode === 'tasas_sfc' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                  >
                    <Landmark className="h-3 w-3" /> Tabla Oficial SFC ({HISTORICO_TASAS_SFC.length} Meses)
                  </Button>
                </div>

                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyMemorial}
                    className="h-7 text-xs gap-1 border-slate-300"
                  >
                    <Copy className="h-3 w-3" /> Copiar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={exportExcelCSV}
                    className="h-7 text-xs gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950"
                  >
                    <FileSpreadsheet className="h-3 w-3" /> Excel / CSV
                  </Button>
                  <Button
                    size="sm"
                    onClick={downloadMemorialTxt}
                    className="h-7 text-xs gap-1 bg-slate-900 hover:bg-slate-800 text-white dark:bg-slate-100 dark:text-slate-900 font-semibold"
                  >
                    <Download className="h-3 w-3" /> TXT
                  </Button>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-0">
              {viewMode === 'tabla' ? (
                /* Tabla de Liquidación Cuota a Cuota */
                <div className="overflow-x-auto max-h-[560px]">
                  <Table>
                    <TableHeader>
                      <TableRow className="text-xs bg-muted/40">
                        <TableHead className="w-12">#</TableHead>
                        <TableHead>Concepto / Cuota</TableHead>
                        <TableHead>Exigibilidad</TableHead>
                        <TableHead>Días Mora</TableHead>
                        <TableHead>Capital Base</TableHead>
                        <TableHead>Tasa Aplicada</TableHead>
                        <TableHead className="text-rose-600 font-bold">Interés Mora</TableHead>
                        <TableHead className="font-bold">Total Cuota</TableHead>
                        <TableHead className="text-right w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {settlementResult.filas.map((fila) => (
                        <TableRow key={fila.numero} className="text-xs hover:bg-muted/30">
                          <TableCell className="font-mono text-muted-foreground">{fila.numero}</TableCell>
                          <TableCell className="font-medium text-foreground">{fila.concepto}</TableCell>
                          <TableCell className="font-mono text-[11px]">{fila.fechaExigibilidad}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50 dark:bg-amber-950">
                              {fila.diasMora} d
                            </Badge>
                          </TableCell>
                          <TableCell className="font-semibold">${fila.capitalBase.toLocaleString('es-CO')}</TableCell>
                          <TableCell>
                            <div>
                              <span className="font-bold text-slate-800 dark:text-slate-200 block text-[11px]">
                                {fila.tasaEM}% EM
                              </span>
                              <span className="text-[9px] text-muted-foreground block">
                                {fila.tasaEA}% EA ({fila.resolucionSFC})
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="font-bold text-rose-600">
                            +${fila.interesMora.toLocaleString('es-CO')}
                          </TableCell>
                          <TableCell className="font-bold text-emerald-700 dark:text-emerald-300">
                            ${fila.saldoFinalFila.toLocaleString('es-CO')}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const target = debtItems[fila.numero - 1];
                                if (target) handleRemoveCuota(target.id);
                              }}
                              className="h-6 w-6 p-0 text-slate-400 hover:text-rose-600"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : viewMode === 'memorial' ? (
                /* Visor del Memorial Formal para el Despacho Judicial */
                <div className="p-5 font-mono text-xs text-slate-800 dark:text-slate-200 whitespace-pre-wrap leading-relaxed max-h-[560px] overflow-y-auto bg-slate-50 dark:bg-slate-900/60 rounded-b-lg">
                  {memorialJudicialTexto}
                </div>
              ) : (
                /* Tabla Maestra de Tasas Oficiales de la Superfinanciera (SFC) */
                <div className="overflow-x-auto max-h-[560px]">
                  <Table>
                    <TableHeader>
                      <TableRow className="text-xs bg-muted/40">
                        <TableHead>Año / Mes</TableHead>
                        <TableHead>Interés Bancario Corriente (IBC E.A.)</TableHead>
                        <TableHead className="font-bold text-rose-600">Tasa de Usura E.A. (1.5x IBC)</TableHead>
                        <TableHead className="font-bold text-emerald-600">Tasa Mensual Vencida (E.M.)</TableHead>
                        <TableHead>Certificación Oficial SFC</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {HISTORICO_TASAS_SFC.map((rate, idx) => (
                        <TableRow key={idx} className="text-xs hover:bg-muted/30">
                          <TableCell className="font-bold">{rate.monthName}</TableCell>
                          <TableCell className="font-mono">{rate.ibc_ea}%</TableCell>
                          <TableCell className="font-mono font-bold text-rose-600">{rate.usura_ea}%</TableCell>
                          <TableCell className="font-mono font-bold text-emerald-600">{rate.tasa_efectiva_mensual}%</TableCell>
                          <TableCell className="text-muted-foreground font-mono text-[11px]">{rate.resolucion_sfc || 'SFC'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
export default FinancialLiquidadorJudicial;
